import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Loader2, Plus, KeyRound, Trash2, Pencil, X, Copy } from "lucide-react";
import { toast } from "sonner";

const inp = "w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent";
const EMPTY = { company_name: "", contact_name: "", email: "", phone: "", password: "" };

export function InstCustomers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [creds, setCreds] = useState(null);

  const load = () => api.get("/inst/customers").then(({ data }) => setRows(data.customers || [])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const reset = async (c) => {
    if (!window.confirm(`Generate a new password for ${c.email}?`)) return;
    try { const { data } = await api.post(`/inst/customers/${c.id}/reset-password`, {}); setCreds(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.company_name} and all of their models?`)) return;
    try { await api.delete(`/inst/customers/${c.id}`); toast.success("Customer deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4">
        <p className="text-sm text-zinc-500">Customer logins for the instrumentation modeling portal.</p>
        <button onClick={() => setEdit(EMPTY)} data-testid="inst-add-customer-btn" className="flex items-center gap-2 bg-[#0F172A] text-white px-4 py-2.5 text-sm hover:bg-[#1E293B] transition-colors shrink-0"><Plus className="h-4 w-4" /> Add customer</button>
      </div>
      <div className="bg-white border border-zinc-200 overflow-x-auto" data-testid="inst-customers-table">
        {loading ? <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div> : (
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-[11px] uppercase tracking-wide text-zinc-500">
              <tr><th className="text-left px-4 py-3">Company</th><th className="text-left px-4 py-3">Contact</th><th className="text-left px-4 py-3">Login email</th><th className="text-left px-4 py-3">Phone</th><th className="text-right px-4 py-3">Models</th><th className="px-4 py-3" /></tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-zinc-400" data-testid="inst-customers-empty">No instrumentation customers yet.</td></tr>}
              {rows.map((c) => (
                <tr key={c.id} data-testid={`inst-customer-row-${c.id}`}>
                  <td className="px-4 py-3 font-medium text-[#0F172A]">{c.company_name}</td>
                  <td className="px-4 py-3">{c.contact_name}</td>
                  <td className="px-4 py-3 font-mono text-xs">{c.email}</td>
                  <td className="px-4 py-3">{c.phone}</td>
                  <td className="px-4 py-3 text-right font-mono">{c.model_count}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-3 text-zinc-400">
                      <button onClick={() => setEdit(c)} title="Edit" data-testid={`inst-customer-edit-${c.id}`} className="hover:text-[#0F172A]"><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => reset(c)} title="Reset password" data-testid={`inst-customer-reset-${c.id}`} className="hover:text-[#D4AF37]"><KeyRound className="h-4 w-4" /></button>
                      <button onClick={() => remove(c)} title="Delete" data-testid={`inst-customer-delete-${c.id}`} className="hover:text-[#EF4444]"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {edit && <CustomerModal initial={edit} onClose={() => setEdit(null)} onSaved={(c) => { setEdit(null); if (c) setCreds(c); load(); }} />}
      {creds && <CredsModal creds={creds} onClose={() => setCreds(null)} />}
    </div>
  );
}

function CustomerModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({ ...EMPTY, ...initial });
  const [saving, setSaving] = useState(false);
  const isNew = !initial.id;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isNew) { const { data } = await api.post("/inst/customers", form); toast.success("Customer login created"); onSaved(data.credentials); }
      else { const { password, ...rest } = form; await api.patch(`/inst/customers/${initial.id}`, rest); toast.success("Customer updated"); onSaved(null); }
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0F172A]/60 backdrop-blur-sm" data-testid="inst-customer-modal">
      <form onSubmit={save} className="bg-white w-full max-w-md">
        <div className="flex items-center justify-between p-5 bg-[#0F172A] text-white">
          <h3 className="font-display font-bold">{isNew ? "New instrumentation customer" : "Edit customer"}</h3>
          <button type="button" onClick={onClose} data-testid="inst-customer-modal-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-6 space-y-3">
          {[["company_name", "Company", true], ["contact_name", "Contact name", true], ["email", "Login email", true], ["phone", "Phone", false]].map(([k, l, req]) => (
            <label key={k} className="block text-sm font-medium text-[#0F172A]">{l}
              <input required={req} type={k === "email" ? "email" : "text"} value={form[k]} onChange={set(k)} data-testid={`inst-customer-${k}`} className={`${inp} mt-1`} />
            </label>
          ))}
          {isNew && (
            <label className="block text-sm font-medium text-[#0F172A]">Password <span className="text-zinc-400 font-normal">(leave blank to generate)</span>
              <input value={form.password} onChange={set("password")} data-testid="inst-customer-password" className={`${inp} mt-1 font-mono`} />
            </label>
          )}
          <button type="submit" disabled={saving} data-testid="inst-customer-save" className="w-full flex justify-center bg-[#0F172A] text-white py-3 text-sm font-medium hover:bg-[#1E293B] disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isNew ? "Create login" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

function CredsModal({ creds, onClose }) {
  const text = `Email: ${creds.email}\nPassword: ${creds.password}\nLogin: ${window.location.origin}/login`;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0F172A]/60 backdrop-blur-sm" data-testid="inst-creds-modal">
      <div className="bg-white w-full max-w-md p-6">
        <h3 className="font-display font-bold text-[#0F172A]">Login credentials</h3>
        <p className="text-xs text-zinc-500 mt-1">Share these with the customer. The password is shown only once.</p>
        <pre className="mt-4 bg-zinc-50 border border-zinc-200 p-4 text-sm font-mono whitespace-pre-wrap" data-testid="inst-creds-text">{text}</pre>
        <div className="mt-4 flex gap-2">
          <button onClick={() => { navigator.clipboard?.writeText(text); toast.success("Copied"); }} data-testid="inst-creds-copy" className="flex-1 flex items-center justify-center gap-2 border border-zinc-300 py-2.5 text-sm hover:bg-zinc-50"><Copy className="h-4 w-4" /> Copy</button>
          <button onClick={onClose} data-testid="inst-creds-close" className="flex-1 bg-[#0F172A] text-white py-2.5 text-sm">Done</button>
        </div>
      </div>
    </div>
  );
}
